<template>
  <div>

    <div class="wz-page-header">
      <h1 class="wz-page-title">Control de asistencia</h1>
    </div>

    <!-- Alertas del colaborador seleccionado (Art. 25 D.L. 728) -->
    <div v-if="alerts.length" class="mb-3">
      <div class="flex items-center justify-between mb-2">
        <h6 class="mb-0 font-bold">Alertas — {{ selectedEmployeeName }}</h6>
        <wz-button variant="light" size="sm" @click="clearAlerts">Cerrar</wz-button>
      </div>
      <wz-alert v-for="(a, i) in alerts" :key="i" variant="warning" icon="ri-error-warning-line" class="mb-2">
        {{ a.message }}
      </wz-alert>
    </div>

    <wz-table-container>

      <wz-table-toolbar>
        <template #left>
          <wz-filter-menu
            :options="filterOptions"
            :selected-option-filter="filters"
            @setFilters="setFilters"
          />
          <div class="month-nav" role="group" aria-label="Navegación de mes">
            <button type="button" class="month-btn" @click="prevMonth" aria-label="Mes anterior">
              <i class="ri-arrow-left-s-line"></i>
            </button>
            <span class="month-label">{{ monthLabel }}</span>
            <button type="button" class="month-btn" @click="nextMonth" aria-label="Mes siguiente">
              <i class="ri-arrow-right-s-line"></i>
            </button>
          </div>
        </template>

        <template #right>
          <wz-tooltip text="Descargar">
            <wz-button variant="outline" size="sm" @click="downloadReport">
              <i class="ri-download-2-line"></i>
            </wz-button>
          </wz-tooltip>
          <div style="width: 240px; flex-shrink: 0">
            <wz-search-bar
              placeholder="Buscar por nombre o DNI"
              @input="search = $event"
              @search="onSearch"
            />
          </div>
          <wz-button variant="outline" size="sm"
            @click="goToUpload">
            <i class="ri-upload-line mr-1"></i>Cargar
          </wz-button>
        </template>

        <template #filters v-if="showFiltersList">
          <wz-badge
            v-for="(val, name) in filterList"
            :key="name"
            variant="primary"
            pill
            dismissible
            @dismiss="deleteFilter(name)"
          >
            {{ getFilterName(name, val) }}
          </wz-badge>
        </template>
      </wz-table-toolbar>

      <div class="p-3">

    <!-- Sin coincidencias de búsqueda (server-side) -->
    <div v-if="!loading && rows.length === 0 && search" class="text-center text-muted py-5">
      <i class="ri-search-line" style="font-size: 3rem; opacity: 0.35;"></i>
      <p class="mt-3 h5">Sin coincidencias para “{{ search }}”</p>
      <p class="text-sm">Probá con otro nombre o número de documento.</p>
    </div>

    <!-- Empty state -->
    <div v-else-if="!loading && rows.length === 0" class="text-center text-muted py-5">
      <i class="ri-time-line" style="font-size: 3rem; opacity: 0.35;"></i>
      <p class="mt-3 h5">No hay registros de asistencia para este mes</p>
      <p class="text-sm">Intenta con otro mes o revisa los filtros aplicados.</p>
    </div>

    <!-- Sin coincidencias del filtro por estado (client-side): hay filas cargadas pero ninguna matchea -->
    <div v-else-if="!loading && visibleRows.length === 0" class="text-center text-muted py-5">
      <i class="ri-filter-off-line" style="font-size: 3rem; opacity: 0.35;"></i>
      <p class="mt-3 h5">Ningún colaborador tiene {{ activeStatusLabel }} en este mes.</p>
      <p class="text-sm">Probá con otro estado o limpiá los filtros.</p>
    </div>

    <!-- Grilla -->
    <div v-else class="attendance-outer">
      <div class="attendance-wrapper">

        <!-- Header row: nombre + días -->
        <div class="att-row att-header" :style="gridStyle">
          <div class="att-col-name att-header-name">Colaborador</div>
          <div
            v-for="d in dayNumbers"
            :key="'h-' + d"
            class="att-header-day"
            :class="{ weekend: isWeekend(d), today: isToday(d) }"
          >
            <div class="day-number">{{ d }}</div>
            <div class="day-letter">{{ dayLetter(d) }}</div>
          </div>
        </div>

        <!-- Filas de empleados -->
        <div
          v-for="row in visibleRows"
          :key="row.employeeId + '_' + row.companyId"
          class="att-row att-data-row"
          :style="gridStyle"
        >
          <!-- Nombre (sticky) — clic para ver alertas del colaborador -->
          <div
            class="att-col-name att-name-clickable"
            :class="{ 'att-name-selected': selectedRowKey === (row.employeeId + '_' + row.companyId) }"
            :title="row.employeeName"
            @click.stop="selectEmployee(row)"
          >
            <div class="emp-name">{{ row.employeeName }}</div>
            <div class="emp-doc">{{ row.employeeIdentifier }}</div>
            <!-- DEF-011: rotula la empresa por fila (un colaborador multi-empresa aparece en varias filas). -->
            <div v-if="row.companyName" class="emp-company">{{ row.companyName }}</div>
          </div>

          <!-- Celda por día -->
          <div
            v-for="d in dayNumbers"
            :key="'c-' + d"
            class="att-day-cell"
            :class="{ weekend: isWeekend(d), today: isToday(d) }"
            @click="openCell(row, d)"
          >
            <div
              v-if="getDay(row, d)"
              class="att-cell-badge"
              :class="{ 'auto-detected': getDay(row, d).isAutoDetected, 'has-tardiness': getDay(row, d).tardinessMinutes > 0 }"
              :style="{ backgroundColor: statusColor(getDay(row, d).status) }"
              :title="cellTooltip(getDay(row, d))"
            >
              {{ statusLetter(getDay(row, d).status) }}
            </div>
          </div>
        </div>

      </div>

      <!-- Leyenda -->
      <div class="att-legend flex flex-wrap items-center mt-3 -mx-2">
        <div v-for="s in statusList" :key="s.key" class="legend-item mr-3 mb-1">
          <span class="legend-dot" :style="{ backgroundColor: s.color }"></span>
          <span class="text-sm ml-1">{{ s.label }}</span>
        </div>
        <div class="legend-item mr-3 mb-1">
          <span class="legend-dot tardiness-dot"></span>
          <span class="text-sm ml-1">Con tardanza</span>
        </div>
        <div class="legend-item mb-1">
          <span class="legend-dot auto-dot"></span>
          <span class="text-sm ml-1">Auto-detectado</span>
        </div>
      </div>
    </div>

      </div>
    </wz-table-container>

    <!-- Modal de edición de celda -->
    <AttendanceEditModal
      v-if="selectedCell"
      :visible="modalVisible"
      :employee-name="selectedCell.employeeName"
      :employee-id="selectedCell.employeeId"
      :record-date="selectedCell.recordDate"
      :day-data="selectedCell.day"
      :company-id="selectedCell.companyId"
      :company-name="selectedCell.companyName"
      @saved="onSaved"
      @propagated="onPropagated"
      @close="modalVisible = false"
    />


  </div>
</template>

<script>
import { Alert, Util, FilterMixin } from "@wizepe/wizeui"
import { AttendanceService, CompanyService, AreaService } from '@/core/services'
import { Constants, downloadErrorMessage } from '@/core/utils'
import fileDownload from 'js-file-download'
import AttendanceEditModal from './components/AttendanceEditModal.vue'

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]

const DAY_LETTERS = ['D', 'L', 'M', 'X', 'J', 'V', 'S']

export default {
  name: 'AttendanceGrid',
  components: { AttendanceEditModal },
  mixins: [FilterMixin],

  data() {
    const now = new Date()
    return {
      currentYear:  now.getFullYear(),
      currentMonth: now.getMonth(),
      rows:      [],
      companies: [],
      areas:     [],
      loading:   false,
      // FilterMixin: los filtros van acá (companyId/areaId server-side; status client-side).
      // Empieza vacío = sin filtros. Los chips y el menú "Filtrar" lo gestionan (igual que Colaboradores).
      filters: {},
      modalVisible: false,
      selectedCell: null,
      // Búsqueda server-side por nombre/DNI (se envía en el filtro a getMonthGrid/export), consistente
      // con Colaboradores/Contratos.
      search: '',
      // Alertas de inasistencias del colaborador seleccionado (clic en el nombre)
      selectedEmployeeId: null,
      selectedEmployeeName: '',
      selectedRowKey: null, // clave compuesta employeeId_companyId de la fila resaltada (multi-empresa)
      alerts: [],
      statusList: Object.values(Constants.AttendanceStatus),
    }
  },

  computed: {
    monthLabel() {
      return `${MONTH_NAMES[this.currentMonth]} ${this.currentYear}`
    },
    daysInMonth() {
      return new Date(this.currentYear, this.currentMonth + 1, 0).getDate()
    },
    dayNumbers() {
      return Array.from({ length: this.daysInMonth }, (_, i) => i + 1)
    },
    gridStyle() {
      return `grid-template-columns: 200px repeat(${this.daysInMonth}, 32px)`
    },
    selectedCompanyName() {
      const c = this.companies.find(x => String(x.key) === String(this.filters.companyId))
      return c ? c.label : ''
    },
    // Opciones del dropdown de estado (los 8 estados reales; sin "Sin registro").
    statusFilterItems() {
      return this.statusList.map(s => ({ key: s.key, label: s.label }))
    },
    // Categorías del menú "Filtrar" (mismo patrón que Colaboradores). companyId/areaId son server-side;
    // status es client-side (ver setFilters/deleteFilter). companies/areas ya vienen como {key,label}.
    filterOptions() {
      return [
        { key: 'companyId', label: 'Empresa', type: Constants.TypeFilters.SINGLE, options: this.companies },
        { key: 'areaId',    label: 'Área',    type: Constants.TypeFilters.SINGLE, options: this.areas },
        { key: 'status',    label: 'Estado',  type: Constants.TypeFilters.SINGLE, options: this.statusFilterItems },
      ]
    },
    // Label del estado seleccionado (para el mensaje del empty state del filtro).
    activeStatusLabel() {
      const s = this.statusList.find(x => x.key === this.filters.status)
      return s ? s.label : ''
    },
    // Filas visibles = rows filtradas por el estado seleccionado (client-side). Sin status → no filtra.
    // Una fila se muestra si tiene AL MENOS UNA celda del mes con ese estado (persistido o auto-detectado,
    // ambos comparten el mismo key de status). Se combina en AND con empresa/área/buscador (ya aplicados
    // server-side sobre `rows`).
    visibleRows() {
      const status = this.filters.status
      if (!status) return this.rows
      return this.rows.filter(row =>
        (row.days || []).some(d => d.status === status))
    },
  },

  methods: {
    async loadData() {
      try {
        this.loading = true
        this.clearAlerts()
        this.$store.dispatch('app/loading', true)
        const payload = {
          year:  this.currentYear,
          month: this.currentMonth + 1,
        }
        if (this.filters.companyId) payload.companyId = this.filters.companyId
        if (this.filters.areaId)    payload.areaId    = this.filters.areaId
        if (this.search)            payload.search    = this.search
        const resp = await AttendanceService.getMonthGrid(payload)
        this.rows = resp.payload || []
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar la grilla de asistencia')
      } finally {
        this.loading = false
        this.$store.dispatch('app/loading', false)
      }
    },

    prevMonth() {
      if (this.currentMonth === 0) { this.currentMonth = 11; this.currentYear-- }
      else this.currentMonth--
      this.loadData()
    },

    nextMonth() {
      if (this.currentMonth === 11) { this.currentMonth = 0; this.currentYear++ }
      else this.currentMonth++
      this.loadData()
    },

    isWeekend(day) {
      const dow = new Date(this.currentYear, this.currentMonth, day).getDay()
      return dow === 0 || dow === 6
    },

    isToday(day) {
      const now = new Date()
      return now.getFullYear() === this.currentYear &&
             now.getMonth()    === this.currentMonth &&
             now.getDate()     === day
    },

    dayLetter(day) {
      return DAY_LETTERS[new Date(this.currentYear, this.currentMonth, day).getDay()]
    },

    getDay(row, day) {
      return (row.days || []).find(d => {
        const date = new Date(d.date + 'T00:00:00')
        return date.getDate() === day
      }) || null
    },

    statusColor(key) {
      const found = Object.values(Constants.AttendanceStatus).find(s => s.key === key)
      return found ? found.color : '#e9ecef'
    },

    statusLetter(key) {
      const found = Object.values(Constants.AttendanceStatus).find(s => s.key === key)
      return found ? found.letter : '?'
    },

    cellTooltip(day) {
      if (!day) return ''
      const s = Object.values(Constants.AttendanceStatus).find(x => x.key === day.status)
      let tip = s ? s.label : day.status
      if (day.entryTime) tip += ` | Entrada: ${day.entryTime}`
      if (day.exitTime)  tip += ` | Salida: ${day.exitTime}`
      if (day.tardinessMinutes > 0) tip += ` | Tardanza: ${day.tardinessMinutes} min`
      return tip
    },

    openCell(row, day) {
      const dateStr = `${this.currentYear}-${String(this.currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      // Cada fila es una relación laboral concreta (un colaborador en una empresa): se pasa su companyId
      // directamente al modal, que lo usa como destino de guardado y de propagación de feriado.
      this.selectedCell = {
        employeeId:   row.employeeId,
        employeeName: row.employeeName,
        recordDate:   dateStr,
        day:          this.getDay(row, day),
        companyId:    row.companyId,
        companyName:  row.companyName,
      }
      this.modalVisible = true
    },

    onSaved() {
      this.modalVisible = false
      this.loadData()
    },

    onPropagated(summary) {
      this.modalVisible = false
      const applied = summary && summary.applied != null ? summary.applied : 0
      const skipped = summary && summary.skipped != null ? summary.skipped : 0
      Alert.success(
        `Feriado aplicado a ${applied} colaborador(es). ` +
        `${skipped} colaborador(es) ya tenían marcación previa y se conservó su estado.`
      )
      this.loadData()
    },

    // Búsqueda server-side (patrón EmployeeList/ContractList): setea el término y recarga desde el backend.
    onSearch(value) {
      this.search = value || ''
      this.loadData()
    },

    async selectEmployee(row) {
      this.selectedEmployeeId = row.employeeId
      this.selectedEmployeeName = row.employeeName
      this.selectedRowKey = row.employeeId + '_' + row.companyId
      try {
        const resp = await AttendanceService.getAlerts(row.employeeId, row.companyId)
        this.alerts = resp.payload || []
      } catch (e) {
        this.alerts = []
        Alert.error('Error al cargar las alertas del colaborador')
      }
    },

    clearAlerts() {
      this.alerts = []
      this.selectedEmployeeId = null
      this.selectedEmployeeName = ''
      this.selectedRowKey = null
    },

    // Override de FilterMixin.setFilters: recarga del backend SOLO si cambió empresa/área. El filtro
    // de estado es client-side (aprobado por PM) → visibleRows se recalcula reactivamente, sin loadData.
    setFilters(newFilters) {
      const prev = this.filters
      const next = JSON.parse(JSON.stringify(newFilters))
      const serverChanged = (prev.companyId || null) !== (next.companyId || null)
        || (prev.areaId || null) !== (next.areaId || null)
      this.filters = next
      this.showFilters = false
      if (serverChanged) this.loadData()
    },

    // Override de FilterMixin.deleteFilter: igual criterio: solo recarga si el chip quitado es server-side.
    deleteFilter(label) {
      const filters = { ...this.filters }
      const queries = { ...this.$route.query }
      delete filters[label]
      delete queries[label]
      this.$router.push({ name: this.$route.name, query: queries }).catch(err => {
        if (err && err.name !== 'NavigationDuplicated') console.error(err)
      })
      this.filters = filters
      if (label === 'companyId' || label === 'areaId') this.loadData()
    },

    // Carga masiva: navega a la vista dedicada (wizard), pasando el mes/año en contexto.
    goToUpload() {
      this.$router.push({
        name: 'attendance-upload',
        query: { year: this.currentYear, month: this.currentMonth + 1 },
      })
    },

    // Descarga el detalle plano de asistencia (una fila por marcación persistida), respetando los
    // filtros de empresa/área/mes de la grilla (mismo patrón que EmployeeList/ContractList).
    async downloadReport() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await AttendanceService.downloadExport(
          this.currentYear, this.currentMonth + 1, this.filters.companyId, this.filters.areaId, this.search)
        fileDownload(resp.data, Util.getFileName(resp))
      } catch (e) {
        Alert.error(await downloadErrorMessage(e) || 'Error al descargar la asistencia')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
  },

  async created() {
    try {
      const [companiesResp, areasResp] = await Promise.all([
        CompanyService.listAll(),
        AreaService.listAll(),
      ])
      const rawCompanies = companiesResp.payload || []
      this.companies = rawCompanies.map(c => ({ key: c.id, label: c.businessName || c.name }))
      this.areas     = (areasResp.payload     || []).map(a => ({ key: a.id, label: a.description }))
    } catch (e) {
      console.error('Error al cargar catálogos', e)
    }
    // Drill-down desde el dashboard (query params): precargar empresa/área como filtros (chips).
    // Se reasigna el objeto completo para que sea reactivo en Vue 2 (filters arranca en {}).
    const { companyId, areaId } = this.$route.query
    const initFilters = {}
    if (companyId && Number(companyId)) initFilters.companyId = Number(companyId)
    if (areaId && Number(areaId))       initFilters.areaId    = Number(areaId)
    this.filters = initFilters
    this.loadData()
  },
}
</script>

<style scoped lang="stylus">

/* ── Contenedor con scroll horizontal ───────────── */
.attendance-outer
  width 100%

.attendance-wrapper
  overflow-x auto
  border 1px solid #dee2e6
  border-radius 6px

/* ── Fila base ───────────────────────────────────── */
.att-row
  display grid
  border-bottom 1px solid #e9ecef

  &:last-child
    border-bottom none

/* ── Columna nombre (sticky) ─────────────────────── */
.att-col-name
  position sticky
  left 0
  background #fff
  z-index 3
  padding 4px 8px
  display flex
  flex-direction column
  justify-content center
  border-right 2px solid #dee2e6
  min-height 48px
  max-width 200px

  .emp-name
    font-size 12px
    font-weight 600
    white-space nowrap
    overflow hidden
    text-overflow ellipsis

  .emp-doc
    font-size 11px
    color #6c757d

  .emp-company
    font-size 10px
    font-weight 600
    color #1976d2
    white-space nowrap
    overflow hidden
    text-overflow ellipsis

/* ── Nombre clickeable (alertas) ─────────────────── */
.att-name-clickable
  cursor pointer

  &:hover
    background #f1f3f5

.att-name-selected
  background #fff3cd

/* ── Header ──────────────────────────────────────── */
.att-header
  background #f8f9fa
  border-bottom 2px solid #dee2e6
  position sticky
  top 0
  z-index 5

.att-header-name
  background #f8f9fa
  font-weight 700
  font-size 11px
  text-transform uppercase
  color #6c757d

.att-header-day
  display flex
  flex-direction column
  align-items center
  justify-content center
  border-right 1px solid #e9ecef
  min-height 44px
  background #f8f9fa
  cursor default

  .day-number
    font-size 11px
    font-weight 700
    color #495057
    line-height 1

  .day-letter
    font-size 10px
    color #adb5bd
    margin-top 2px

  &.weekend
    background #efefef
    .day-number
      color #adb5bd
    .day-letter
      color #ced4da

  &.today
    background #e3f2fd
    .day-number
      color #1976d2

/* ── Celda de día ────────────────────────────────── */
.att-day-cell
  display flex
  align-items center
  justify-content center
  border-right 1px solid #f0f0f0
  min-height 48px
  cursor pointer
  transition background 0.1s

  &:hover
    background #f8f9fa

  &.weekend
    background #fafafa

  &.today
    background #e8f4fd

/* ── Badge de status ─────────────────────────────── */
.att-cell-badge
  width 26px
  height 26px
  border-radius 4px
  display flex
  align-items center
  justify-content center
  font-size 9px
  font-weight 700
  color #fff
  user-select none
  transition box-shadow 0.1s

  &.auto-detected
    opacity 0.65
    border 1px dashed rgba(0,0,0,0.25)

  &.has-tardiness
    box-shadow 0 2px 0 0 #ffc107

.att-day-cell:hover .att-cell-badge
  box-shadow 0 0 0 2px rgba(0,0,0,0.2)

/* ── Navegación de mes ───────────────────────────── */
/* Navegación de mes: control segmentado compacto (un solo borde, botones sin borde con divisores). */
.month-nav
  display inline-flex
  align-items center
  height 32px
  border 1px solid #dee2e6
  border-radius 6px
  background #fff
  overflow hidden

  .month-btn
    width 28px
    height 100%
    border none
    background transparent
    cursor pointer
    display flex
    align-items center
    justify-content center
    font-size 16px
    color #6b7280
    padding 0
    transition background 0.1s, color 0.1s

    &:hover
      background #f3f4f6
      color #111827

  .month-label
    font-size 13px
    font-weight 600
    min-width 104px
    text-align center
    color #374151
    padding 0 6px
    border-left 1px solid #eef0f2
    border-right 1px solid #eef0f2
    white-space nowrap

/* ── Leyenda ─────────────────────────────────────── */
.att-legend
  .legend-item
    display flex
    align-items center

  .legend-dot
    width 14px
    height 14px
    border-radius 3px
    display inline-block
    flex-shrink 0

  .tardiness-dot
    background #ffc107
    height 4px
    border-radius 2px

  .auto-dot
    background #adb5bd
    opacity 0.65
    border 1px dashed rgba(0,0,0,0.25)

/* ── Modal de carga ──────────────────────────────── */
.upload-errors
  max-height 200px
  overflow-y auto
  padding-left 1rem

.alert-sm
  font-size 13px
  padding 10px 14px
</style>
